"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  computeGcfLcm,
  type GcfLcmResult,
  type GcfStage,
  MAX_INTEGER_DIGITS,
  MAX_LIST_LENGTH,
  parseIntegerList,
  productFromRows,
} from "@/lib/tool-kit/math/gcf-lcm";
import { exponentForm, toSuperscript } from "@/lib/tool-kit/math/primes";

/** English strings. A translated content file overrides these through `ui`. */
export const GCF_LCM_CALCULATOR_STRINGS = {
  numbers: "Whole numbers",
  numbersHint:
    "Two or more, separated by commas or spaces. Signs are ignored: both are defined on absolute values.",
  numbersPlaceholder: "12, 18, 30",
  gcfSymbol: "GCF",
  lcmSymbol: "LCM",
  results: "Results",
  gcf: "Greatest common factor (GCF)",
  lcm: "Least common multiple (LCM)",
  count: "Numbers compared",
  coprime:
    "The GCF is 1, so these numbers are coprime (relatively prime): they share no factor except 1.",
  euclidHeading: "GCF by Euclid's algorithm",
  euclidIntro:
    "Divide the larger number by the smaller, then divide the last divisor by the remainder, until the remainder is 0. The last divisor is the GCF.",
  euclidResult: "The remainder reached 0, so {pair} = {gcf}.",
  chain: "With more than two numbers, fold them in one at a time: {chain}.",
  lcmHeading: "LCM from the GCF",
  lcmIntro:
    "For two numbers, LCM(a, b) = a × b ÷ GCF(a, b). With more, fold them in one at a time.",
  primeHeading: "The prime factorization method",
  primeIntro:
    "Factor each number into primes. The GCF takes every prime the numbers share at its lowest power; the LCM takes every prime that appears at its highest power.",
  primeColumn: "Prime",
  gcfColumn: "GCF (lowest power)",
  lcmColumn: "LCM (highest power)",
  absent: "—",
  tooLargeToFactor:
    "At least one number is above 10²⁰, too large to factor quickly. Euclid's algorithm above still gives the exact answer.",
  check: "Check: GCF × LCM = {gcf} × {lcm} = {product}, the same as {a} × {b}.",
  signNote:
    "Minus signs were dropped: the GCF and LCM are defined on absolute values.",
  enterNumbers: "Enter at least two whole numbers to find their GCF and LCM.",
  notWhole:
    "These are not whole numbers: {tokens}. Remove decimals, fractions and thousands separators.",
  zero: "0 is left out of this calculator: every number divides 0, so GCF(0, n) = n, and LCM(0, n) is 0 by convention. Remove the 0 to compare the other numbers.",
  tooMany: "Enter at most {max} numbers, each at most {digits} digits long.",
  summary: "{gcfSymbol}({list}) = {gcf}; {lcmSymbol}({list}) = {lcm}",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

type Strings = typeof GCF_LCM_CALCULATOR_STRINGS;

const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";

function list(values: readonly bigint[]): string {
  return values.map(String).join(", ");
}

function power(prime: bigint, exponent: number, absent: string): string {
  if (exponent === 0) {
    return absent;
  }
  return exponent === 1 ? String(prime) : `${prime}${toSuperscript(exponent)}`;
}

function EuclidStage({
  stage,
  strings,
}: {
  stage: GcfStage;
  strings: Strings;
}) {
  const pair = `${strings.gcfSymbol}(${stage.left}, ${stage.right})`;
  return (
    <div className={MONO_LINES}>
      <p className="font-medium text-foreground">{pair}</p>
      {stage.steps.map((step) => (
        <p key={`${step.dividend}-${step.divisor}`}>
          {String(step.dividend)} = {String(step.quotient)} ×{" "}
          {String(step.divisor)} + {String(step.remainder)}
        </p>
      ))}
      <p className="font-sans text-foreground">
        {t(strings.euclidResult, { pair, gcf: String(stage.gcd) })}
      </p>
    </div>
  );
}

/** "GCF(GCF(12, 18), 30) = GCF(6, 30) = 6" */
function chainText(result: GcfLcmResult, symbol: string): string {
  const [first, second, ...rest] = result.values;
  let nested = `${symbol}(${first}, ${second})`;
  for (const value of rest) {
    nested = `${symbol}(${nested}, ${value})`;
  }
  const last = result.gcfStages.at(-1);
  return `${nested} = ${symbol}(${last?.left}, ${last?.right}) = ${result.gcf}`;
}

function PrimeMethod({
  result,
  strings,
}: {
  result: GcfLcmResult;
  strings: Strings;
}) {
  const rows = result.factorRows;
  if (rows === null || result.factorizations === null) {
    return <Notice>{strings.tooLargeToFactor}</Notice>;
  }
  const gcfFactors = productFromRows(rows, "min");
  const lcmFactors = productFromRows(rows, "max");
  return (
    <>
      <div className={MONO_LINES}>
        {result.values.map((value, index) => (
          // The same number can be entered twice; position is its identity.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional inputs
          <p key={index}>
            {String(value)} ={" "}
            {exponentForm(result.factorizations?.[index] ?? []) || "1"}
          </p>
        ))}
      </div>
      <DataTable
        head={[
          strings.primeColumn,
          ...result.values.map(String),
          strings.gcfColumn,
          strings.lcmColumn,
        ]}
        rows={rows.map((row) => [
          String(row.prime),
          ...row.exponents.map((e) => power(row.prime, e, strings.absent)),
          power(row.prime, row.min, strings.absent),
          power(row.prime, row.max, strings.absent),
        ])}
      />
      <div className={MONO_LINES}>
        <p>
          {strings.gcfSymbol} = {exponentForm(gcfFactors) || "1"} ={" "}
          {String(result.gcf)}
        </p>
        <p>
          {strings.lcmSymbol} = {exponentForm(lcmFactors) || "1"} ={" "}
          {String(result.lcm)}
        </p>
      </div>
    </>
  );
}

function Results({
  result,
  strings,
}: {
  result: GcfLcmResult;
  strings: Strings;
}) {
  const values = result.values;
  const summary = t(strings.summary, {
    gcfSymbol: strings.gcfSymbol,
    lcmSymbol: strings.lcmSymbol,
    list: list(values),
    gcf: String(result.gcf),
    lcm: String(result.lcm),
  });
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.gcf} value={String(result.gcf)} />
        <StatCard emphasis label={strings.lcm} value={String(result.lcm)} />
        <StatCard label={strings.count} value={values.length} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      {result.gcf === BigInt(1) ? <Notice>{strings.coprime}</Notice> : null}
      {result.hadNegative ? <Notice>{strings.signNote}</Notice> : null}

      <h3 className={WIDGET_H3}>{strings.euclidHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.euclidIntro}</p>
      {result.gcfStages.map((stage, index) => (
        // Stages are positional: GCF(6, 6) can occur twice.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional stages
        <EuclidStage key={index} stage={stage} strings={strings} />
      ))}
      {values.length > 2 ? (
        <p className="text-muted-foreground text-sm">
          {t(strings.chain, { chain: chainText(result, strings.gcfSymbol) })}
        </p>
      ) : null}

      <h3 className={WIDGET_H3}>{strings.lcmHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.lcmIntro}</p>
      <div className={MONO_LINES}>
        {result.lcmStages.map((stage, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional stages
          <p key={index}>
            {strings.lcmSymbol}({String(stage.left)}, {String(stage.right)}) ={" "}
            {String(stage.left)} × {String(stage.right)} ÷ {String(stage.gcd)} ={" "}
            {String(stage.lcm)}
          </p>
        ))}
      </div>
      {values.length === 2 ? (
        <p className="text-muted-foreground text-sm">
          {t(strings.check, {
            gcf: String(result.gcf),
            lcm: String(result.lcm),
            product: String(result.gcf * result.lcm),
            a: String(values[0]),
            b: String(values[1]),
          })}
        </p>
      ) : null}

      <h3 className={WIDGET_H3}>{strings.primeHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.primeIntro}</p>
      <PrimeMethod result={result} strings={strings} />
    </>
  );
}

/** The list, read and solved once per distinct input (the expensive part is factoring). */
function useGcfLcm(raw: string) {
  return useMemo(() => {
    const parsed = parseIntegerList(raw);
    const solvable =
      parsed.invalid.length === 0 &&
      !parsed.hasZero &&
      !parsed.tooLong &&
      parsed.values.length >= 2;
    return { parsed, result: solvable ? computeGcfLcm(parsed.values) : null };
  }, [raw]);
}

function Outcome({ raw, strings }: { raw: string; strings: Strings }) {
  const { parsed, result } = useGcfLcm(raw);
  if (parsed.invalid.length > 0) {
    return (
      <ErrorNotice>
        {t(strings.notWhole, { tokens: parsed.invalid.join(", ") })}
      </ErrorNotice>
    );
  }
  if (parsed.hasZero) {
    return <ErrorNotice>{strings.zero}</ErrorNotice>;
  }
  if (parsed.tooLong) {
    return (
      <ErrorNotice>
        {t(strings.tooMany, {
          max: MAX_LIST_LENGTH,
          digits: MAX_INTEGER_DIGITS,
        })}
      </ErrorNotice>
    );
  }
  if (result === null) {
    return <Notice>{strings.enterNumbers}</Notice>;
  }
  return <Results result={result} strings={strings} />;
}

export default function GcfLcmCalculatorWidget() {
  const strings = useToolUi(GCF_LCM_CALCULATOR_STRINGS);
  const [raw, setRaw] = useState("12, 18, 30");
  // Factoring twelve 20-digit numbers can take a moment; keep typing responsive.
  const deferred = useDeferredValue(raw);
  return (
    <>
      <TextField
        hint={strings.numbersHint}
        id="gcf-numbers"
        label={strings.numbers}
        mono
        onChange={setRaw}
        placeholder={strings.numbersPlaceholder}
        value={raw}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome raw={deferred} strings={strings} />
      </section>
    </>
  );
}
